/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-64 — the receipt template: what a shop may change about its ticket
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * FIXED BLOCKS, NOT A CANVAS — and that is a safety property, not a shortcut.
 *
 * OD-13 is the rule this whole feature lives under: the printed ticket is a
 * COURTESY ticket and must never impersonate an invoice. No "Factura" heading,
 * no sequential number, no deductible-looking VAT block. That is what keeps the
 * CRM out of invoicing-software territory and away from Verifactu obligations.
 *
 * A free-form designer would make that rule unenforceable: anyone could type
 * "Factura Nº 001" into a text box and no code could stop them. So a shop
 * arranges a KNOWN set of blocks instead — reorder, show, hide, align, size —
 * and the only free text is a thank-you message. The owner approved this shape
 * over the blank canvas on 2026-09-11.
 *
 * The footer is not in the shop's gift at all: see `FOOTER_TEXT`.
 */

/** 58 mm thermal paper is 384 dots wide. Every measurement here is in dots. */
export const RECEIPT_WIDTH_DOTS = 384;

/**
 * The one line a shop cannot remove, reword, hide or reorder away from the end.
 *
 * It is the single sentence that makes everything above it safe to let a shop
 * change: whatever they arrange, the ticket still says plainly that it is not
 * an invoice. Enforced in three places on purpose — the layout always appends
 * it, the builder never offers it as a block, and the server rejects a template
 * that tries to carry its own. A rule with one lock is a rule waiting to be
 * bypassed.
 */
export const FOOTER_TEXT = "Ticket de compra — no válido como factura";

/**
 * Every block the renderer knows how to draw. Adding to this union is a
 * deliberate act: the renderer must learn to lay it out, and the builder must
 * learn to offer it.
 */
export type ReceiptBlockId =
  | "logo"
  | "shopName"
  | "address"
  /** G-203 (OD-402): the shop's NIF, printed only when the shop has one. */
  | "nif"
  | "dateTime"
  | "lines"
  | "total"
  | "payment"
  | "servedBy"
  | "message";

export type ReceiptAlign = "left" | "center" | "right";

/** Three steps, not a free pixel size — a 384-dot roll has no room for more. */
export type ReceiptSize = "small" | "normal" | "large";

export interface ReceiptBlock {
  id: ReceiptBlockId;
  visible: boolean;
  align: ReceiptAlign;
  size: ReceiptSize;
  /**
   * Only meaningful for `message`, the one free-text block. Length is bounded
   * by the server; a paragraph on a 58 mm roll is not a design choice, it is
   * an accident.
   */
  text?: string;
}

export interface ReceiptTemplate {
  /** Order IS the printed order. The footer is appended after all of these. */
  blocks: ReceiptBlock[];
  /** The shop's uploaded logo, or null to print without one. */
  logoKey: string | null;
  /** Logo width as a fraction of the paper, 0.2–1. Height follows the aspect. */
  logoScale: number;
}

/**
 * What a shop gets before it touches anything: a sensible ticket that already
 * satisfies OD-13. A shop that never opens the builder still prints something
 * correct, which is the point of having a default at all.
 */
export const DEFAULT_TEMPLATE: ReceiptTemplate = {
  logoKey: null,
  logoScale: 0.5,
  blocks: [
    { id: "logo", visible: true, align: "center", size: "normal" },
    { id: "shopName", visible: true, align: "center", size: "large" },
    { id: "address", visible: true, align: "center", size: "small" },
    { id: "nif", visible: true, align: "center", size: "small" },
    { id: "dateTime", visible: true, align: "center", size: "small" },
    { id: "lines", visible: true, align: "left", size: "normal" },
    { id: "total", visible: true, align: "right", size: "large" },
    { id: "payment", visible: true, align: "left", size: "normal" },
    { id: "servedBy", visible: true, align: "center", size: "small" },
    {
      id: "message",
      visible: true,
      align: "center",
      size: "normal",
      text: "¡Gracias por su compra!",
    },
  ],
};

/** Human labels for the builder. Spanish is the PRINTED language (owner,
 *  2026-09-10); the app's own interface stays English, as G-27 records. */
export const BLOCK_LABELS: Record<ReceiptBlockId, string> = {
  logo: "Logo",
  shopName: "Shop name",
  address: "Address",
  nif: "NIF",
  dateTime: "Date and time",
  lines: "What was bought",
  total: "Total",
  payment: "How it was paid",
  servedBy: "Served by",
  message: "Your message",
};

/**
 * Repairs anything stored, rather than trusting it.
 *
 * A template is data that has been sitting in a database across deploys, so it
 * may predate a block, carry one that no longer exists, or have been hand-edited.
 * The renderer must never be handed a shape it cannot draw, and a shop must
 * never lose its ticket because a block was renamed: unknown blocks are dropped,
 * missing ones are appended hidden, and the result is always printable.
 */
export function normalizeTemplate(
  input: Partial<ReceiptTemplate> | null | undefined,
): ReceiptTemplate {
  const known = new Set<ReceiptBlockId>(
    DEFAULT_TEMPLATE.blocks.map((b) => b.id),
  );
  const seen = new Set<ReceiptBlockId>();

  const blocks: ReceiptBlock[] = [];
  for (const raw of input?.blocks ?? []) {
    if (!raw || !known.has(raw.id) || seen.has(raw.id)) continue;
    seen.add(raw.id);
    const fallback = DEFAULT_TEMPLATE.blocks.find((b) => b.id === raw.id)!;
    blocks.push({
      id: raw.id,
      visible: typeof raw.visible === "boolean" ? raw.visible : fallback.visible,
      align: (["left", "center", "right"] as const).includes(raw.align)
        ? raw.align
        : fallback.align,
      size: (["small", "normal", "large"] as const).includes(raw.size)
        ? raw.size
        : fallback.size,
      ...(raw.id === "message"
        ? { text: (raw.text ?? fallback.text ?? "").slice(0, 120) }
        : {}),
    });
  }

  // A block this version knows about but the stored template does not: append
  // it HIDDEN, so an upgrade never silently changes what a shop prints.
  for (const fallback of DEFAULT_TEMPLATE.blocks) {
    if (!seen.has(fallback.id)) blocks.push({ ...fallback, visible: false });
  }

  const scale = Number(input?.logoScale);
  return {
    blocks,
    logoKey: input?.logoKey ?? null,
    logoScale: Number.isFinite(scale) ? Math.min(1, Math.max(0.2, scale)) : 0.5,
  };
}

/**
 * What the API stores for a shop, as either page reads it: `blocks` is null
 * when the shop never opened the builder.
 */
export interface StoredTemplateLike {
  blocks: unknown[] | null;
  logoKey: string | null;
  logoScale: number;
}

/**
 * The printable template for a stored row — ONE rule for the office preview
 * and the kiosk's print (G-156). A shop that never opened the builder gets
 * the default blocks but keeps its OWN logo settings: spreading the default
 * wholesale once replaced a stored scale with 0.5 on the office page, and a
 * second copy of this choice in the print path is how the two tickets would
 * drift. `normalizeTemplate` repairs anything stored by an older renderer.
 */
export function templateFromStored(stored: StoredTemplateLike): ReceiptTemplate {
  return stored.blocks
    ? normalizeTemplate({
        blocks: stored.blocks as ReceiptTemplate["blocks"],
        logoKey: stored.logoKey,
        logoScale: stored.logoScale,
      })
    : { ...DEFAULT_TEMPLATE, logoKey: stored.logoKey, logoScale: stored.logoScale };
}
